import React, { useState, useEffect, useRef } from "react";
import Container from "./container";
import SectionTitle from './sectionTitle';
import { useTranslation } from 'react-i18next';
import axios from "axios";

const Stats = () => {
  const { t } = useTranslation();
  const apiCallMade = useRef(false);
  
  // Starting default values
  const defaultValues = {
    COUNT_ACTORS: 38000,
    COUNT_COMPANIES: 13000,
    AVG_DAILY_USERS_LAST_YEAR: 450
  };

  // For displaying formatted values
  const [statsData, setStatsData] = useState({
    COUNT_ACTORS: `${defaultValues.COUNT_ACTORS.toLocaleString()} +`,
    COUNT_COMPANIES: `${defaultValues.COUNT_COMPANIES.toLocaleString()} +`,
    AVG_DAILY_USERS_LAST_YEAR: `${defaultValues.AVG_DAILY_USERS_LAST_YEAR.toLocaleString()} +`
  });
  
  const [loading, setLoading] = useState(true);
  const [animatedValues, setAnimatedValues] = useState({
    COUNT_ACTORS: defaultValues.COUNT_ACTORS,
    COUNT_COMPANIES: defaultValues.COUNT_COMPANIES,
    AVG_DAILY_USERS_LAST_YEAR: defaultValues.AVG_DAILY_USERS_LAST_YEAR
  });

  // Récupération des identifiants depuis le fichier .env
  const apiUsername = process.env.REACT_APP_API_USERNAME;
  const apiPassword = process.env.REACT_APP_API_PASSWORD;
  const auth = { username: apiUsername, password: apiPassword };

  // Function to round numbers according to modulo requirements
  const roundWithModulo = (value, mod) => {
    return Math.floor(value / mod) * mod;
  };

  // Function to animate counting from default to target numbers
  const animateNumbers = (targets) => {
    const duration = 1500; // Animation duration in ms
    const frameDuration = 16; // ~60fps
    const totalFrames = Math.round(duration / frameDuration);
    let frame = 0;
    
    // Start from default values
    const startValues = {
      COUNT_ACTORS: defaultValues.COUNT_ACTORS,
      COUNT_COMPANIES: defaultValues.COUNT_COMPANIES,
      AVG_DAILY_USERS_LAST_YEAR: defaultValues.AVG_DAILY_USERS_LAST_YEAR
    };

    const animate = () => {
      frame++;
      const progress = frame / totalFrames;
      
      // Linear interpolation between start and target values
      setAnimatedValues({
        COUNT_ACTORS: Math.floor(startValues.COUNT_ACTORS + progress * (targets.COUNT_ACTORS - startValues.COUNT_ACTORS)),
        COUNT_COMPANIES: Math.floor(startValues.COUNT_COMPANIES + progress * (targets.COUNT_COMPANIES - startValues.COUNT_COMPANIES)),
        AVG_DAILY_USERS_LAST_YEAR: Math.floor(startValues.AVG_DAILY_USERS_LAST_YEAR + progress * (targets.AVG_DAILY_USERS_LAST_YEAR - startValues.AVG_DAILY_USERS_LAST_YEAR))
      });

      if (frame < totalFrames) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  useEffect(() => {
    // Ensure API call is made only once
    if (apiCallMade.current) return;
    
    const fetchStats = async () => {
      try {
        setLoading(true);
        apiCallMade.current = true;
        
        // Use the correct URL format
        const url = "https://api.osiris.brussels/geoserver/cdco/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=cdco%3ACDCO_STATS&outputFormat=application%2Fjson";
        
        const response = await axios.get(url, { 
          auth,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        // Check if we have data
        if (response.data.features && response.data.features.length > 0) {
          const stats = response.data.features[0].properties;
          
          // Apply modulo operations
          const actorsRounded = roundWithModulo(stats.COUNT_ACTORS, 100);
          const companiesRounded = roundWithModulo(stats.COUNT_COMPANIES, 100);
          const usersRounded = roundWithModulo(stats.AVG_DAILY_USERS_LAST_YEAR, 10);
          
          // Start animation with the rounded values
          animateNumbers({
            COUNT_ACTORS: actorsRounded,
            COUNT_COMPANIES: companiesRounded,
            AVG_DAILY_USERS_LAST_YEAR: usersRounded
          });
          
          // Format the data with "+" suffix for final state
          setStatsData({
            COUNT_ACTORS: `${actorsRounded.toLocaleString()} +`,
            COUNT_COMPANIES: `${companiesRounded.toLocaleString()} +`,
            AVG_DAILY_USERS_LAST_YEAR: `${usersRounded.toLocaleString()}+`
          });
        }
      } catch (error) {
        // Keeping only one console log for errors
        console.error(t("api-request-error-message"), ":", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []); // Empty dependency array to run only once

  // Format animated values for display
  const getFormattedValue = (key) => {
    // Format with thousands separator
    return `${animatedValues[key].toLocaleString()} +`;
  };

  return (
    <>
      <Container className="flex flex-row flex-wrap lg:flex-nowrap">
        <SectionTitle title={"120 000 +"} posttitle={t('stat_road_usage')}>
          {t('stat_road_usage_desc')}
        </SectionTitle>
        <SectionTitle title={getFormattedValue('COUNT_ACTORS')} posttitle={t('stat_subscribed_users')}>
          {t('stat_subscribed_users_desc')}
        </SectionTitle>
        <SectionTitle title={getFormattedValue('COUNT_COMPANIES')} posttitle={t('stat_subscribed_enterprises')}>
          {t('stat_subscribed_enterprises_desc')}
        </SectionTitle>
        <SectionTitle title={getFormattedValue('AVG_DAILY_USERS_LAST_YEAR')} posttitle={t('stat_daily_users')} className="dark: text-white">
          {t('stat_daily_users_desc')}
        </SectionTitle>
      </Container>
    </>
  );
};

export default Stats;